/**
 * 배지 컴포넌트
 *
 * 상태, 라벨, 태그 등을 표시하는 작은 배지 컴포넌트입니다.
 * 다양한 시각적 변형(variant)을 제공합니다.
 *
 * @component
 * @example
 * // 상태 표시
 * <AppBadge variant="success">완료</AppBadge>
 * <AppBadge variant="error">실패</AppBadge>
 * <AppBadge variant="warning">대기 중</AppBadge>
 *
 * @example
 * // 트랙 상태 목록
 * <div className="flex gap-2">
 *   {tracks.map(track => (
 *     <AppBadge key={track.id} variant={track.statusVariant}>
 *       {track.statusLabel}
 *     </AppBadge>
 *   ))}
 * </div>
 *
 * @typedef {object} AppBadgeProps
 * @property {'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'neutral'} [variant='neutral'] - 배지 스타일
 * @property {string} [className] - 추가 CSS 클래스
 * @property {React.ReactNode} children - 배지 텍스트
 *
 * @returns {JSX.Element} 스타일이 적용된 배지
 */

import { HTMLAttributes, forwardRef } from 'react';
import { cn } from '@/lib/utils';

export type AppBadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'neutral';

export interface AppBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: AppBadgeVariant;
}

export const AppBadge = forwardRef<HTMLSpanElement, AppBadgeProps>(
  ({ variant = 'neutral', className, children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn('badge', `badge-${variant}`, className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

AppBadge.displayName = 'AppBadge';
